'use client'

import { useRef } from 'react'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { AiStep } from '@/lib/commands/graph-serialize'
import { TemplateChipBar } from '../TemplateChipBar'

const modelOptions = [
  { label: 'Default (workspace model)', value: 'default' },
  { label: 'Sonnet', value: 'claude-sonnet-4-6' },
  { label: 'Opus', value: 'claude-opus-4-1' },
  { label: 'Haiku', value: 'claude-haiku-4-5' },
]

export function AiStepEditor({
  step,
  priorStepIds,
  includeEvent,
  onChange,
}: {
  step: AiStep
  priorStepIds: string[]
  includeEvent: boolean
  onChange: (step: AiStep) => void
}) {
  const promptRef = useRef<HTMLTextAreaElement | null>(null)

  const insertPromptToken = (token: string) => {
    const textarea = promptRef.current
    if (!textarea) {
      onChange({ ...step, prompt: `${step.prompt}${token}` })
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    onChange({ ...step, prompt: `${step.prompt.slice(0, start)}${token}${step.prompt.slice(end)}` })
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${step.id}-prompt`}>Prompt</Label>
        <TemplateChipBar stepIds={priorStepIds} includeEvent={includeEvent} onInsert={insertPromptToken} />
        <Textarea
          ref={promptRef}
          id={`${step.id}-prompt`}
          className="min-h-32"
          value={step.prompt}
          onChange={(event) => onChange({ ...step, prompt: event.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label>Model</Label>
        <Select
          value={step.model ?? 'default'}
          onValueChange={(value) => onChange(value === 'default' ? withoutModel(step) : { ...step, model: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {modelOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

function withoutModel(step: AiStep): AiStep {
  return { id: step.id, kind: 'ai_step', prompt: step.prompt }
}
